import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  FileText, Plus, Settings, LogOut, Zap, TrendingUp, Clock, FolderOpen, Calendar,
  Users, Briefcase, UserX, Building2, AlertCircle, ArrowRight, CheckCircle
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // CRM-specific state
  const [crmStats, setCrmStats] = useState({
    clients: 0,
    cases: 0,
    opposingCounsels: 0,
    attorneys: 0
  });
  const [cases, setCases] = useState([]);
  const [upcomingHearings, setUpcomingHearings] = useState([]);

  useEffect(() => {
    fetchUserData();
    fetchTemplates();
    fetchRuns();
  }, []);
  
  useEffect(() => {
    if (user?.app_mode === 'crm') {
      fetchCrmData();
    }
  }, [user]);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch user data');
      const data = await response.json();
      setUser(data);
    } catch (error) {
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/templates`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchRuns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/runs`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRuns(data);
      }
    } catch (error) {
      console.error('Failed to fetch runs:', error);
    }
  };

  const fetchCrmData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [clientsRes, casesRes, counselsRes, firmRes] = await Promise.all([
        fetch(`${API}/crm/clients`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/crm/cases`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/crm/opposing-counsel`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/crm/firm`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      const clients = clientsRes.ok ? await clientsRes.json() : [];
      const cases = casesRes.ok ? await casesRes.json() : [];
      const counsels = counselsRes.ok ? await counselsRes.json() : [];
      const firmData = firmRes.ok ? await firmRes.json() : {};
      
      setCrmStats({
        clients: clients.length,
        cases: cases.length,
        opposingCounsels: counsels.length,
        attorneys: firmData.attorneys?.length || 0
      });
      
      setCases(cases);
      
      // Get upcoming hearings (next 30 days)
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      const upcoming = cases.filter(c => {
        if (!c.next_hearing_date) return false;
        const hearingDate = new Date(c.next_hearing_date);
        return hearingDate >= now && hearingDate <= thirtyDaysFromNow;
      }).sort((a, b) => new Date(a.next_hearing_date) - new Date(b.next_hearing_date));
      
      setUpcomingHearings(upcoming);
    } catch (error) {
      console.error('Error fetching CRM data:', error);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      localStorage.removeItem('token');
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  const planInfo = {
    free: { color: 'bg-slate-500', name: 'Free' },
    pro: { color: 'bg-violet-600', name: 'Professional' },
    business: { color: 'bg-indigo-600', name: 'Business' },
    crm: { color: 'bg-violet-600', name: 'CRM Professional' }
  };

  const currentPlan = planInfo[user?.subscription_plan] || planInfo.free;

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} onLogout={handleLogout} />
      
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8 lg:pt-8 pt-20">{/* pt-20 for mobile header */}

        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-slate-600 text-lg">
            {user?.app_mode === 'crm' 
              ? 'Manage your practice and cases' 
              : 'Manage your document workflows and templates'
            }
          </p>
        </div>

        {/* Conditional Dashboard Content */}
        {user?.app_mode === 'crm' ? (
          /* CRM Dashboard */
          <div className="space-y-8">
            {/* CRM Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Clients */}
              <div 
                onClick={() => navigate('/clients')}
                className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg hover:border-violet-300 hover:shadow-xl transition-all cursor-pointer group"
              >
                <Users className="w-10 h-10 text-violet-600 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-sm font-medium text-slate-600 mb-1">Total Clients</h3>
                <p className="text-3xl font-bold text-slate-900">{crmStats.clients}</p>
                <p className="text-xs text-violet-600 mt-2 flex items-center gap-1">
                  View all <ArrowRight className="w-3 h-3" />
                </p>
              </div>

              {/* Cases */}
              <div 
                onClick={() => navigate('/cases')}
                className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg hover:border-violet-300 hover:shadow-xl transition-all cursor-pointer group"
              >
                <Briefcase className="w-10 h-10 text-indigo-600 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-sm font-medium text-slate-600 mb-1">Total Cases</h3>
                <p className="text-3xl font-bold text-slate-900">{crmStats.cases}</p>
                <p className="text-xs text-indigo-600 mt-2 flex items-center gap-1">
                  View all <ArrowRight className="w-3 h-3" />
                </p>
              </div>

              {/* Opposing Counsel */}
              <div 
                onClick={() => navigate('/opposing-counsel')}
                className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg hover:border-violet-300 hover:shadow-xl transition-all cursor-pointer group"
              >
                <UserX className="w-10 h-10 text-orange-600 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-sm font-medium text-slate-600 mb-1">Opposing Counsel</h3>
                <p className="text-3xl font-bold text-slate-900">{crmStats.opposingCounsels}</p>
                <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                  View all <ArrowRight className="w-3 h-3" />
                </p>
              </div>

              {/* Attorneys */}
              <div 
                onClick={() => navigate('/firm')}
                className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg hover:border-violet-300 hover:shadow-xl transition-all cursor-pointer group"
              >
                <Building2 className="w-10 h-10 text-teal-600 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-sm font-medium text-slate-600 mb-1">Firm Attorneys</h3>
                <p className="text-3xl font-bold text-slate-900">{crmStats.attorneys}</p>
                <p className="text-xs text-teal-600 mt-2 flex items-center gap-1">
                  Manage firm <ArrowRight className="w-3 h-3" />
                </p>
              </div>
            </div>

            {/* Case Status Breakdown */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-lg">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-violet-600" />
                Case Status Overview
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {['Active', 'Pending', 'Closed', 'On Hold'].map(status => {
                  const count = cases.filter(c => c.status === status).length;
                  const colors = {
                    'Active': 'border-green-200 bg-green-50 text-green-700',
                    'Pending': 'border-yellow-200 bg-yellow-50 text-yellow-700',
                    'Closed': 'border-slate-200 bg-slate-50 text-slate-700',
                    'On Hold': 'border-orange-200 bg-orange-50 text-orange-700'
                  };
                  return (
                    <div key={status} className={`p-4 rounded-xl border-2 ${colors[status]}`}>
                      <p className="text-sm font-medium mb-1">{status}</p>
                      <p className="text-3xl font-bold">{count}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Upcoming Hearings */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-violet-600" />
                  Upcoming Hearings (Next 30 Days)
                </h2>
                <Button
                  onClick={() => navigate('/cases')}
                  variant="outline"
                  size="sm"
                  className="rounded-lg"
                >
                  View All Cases
                </Button>
              </div>
              {upcomingHearings.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No upcoming hearings in the next 30 days</p>
              ) : (
                <div className="space-y-3">
                  {upcomingHearings.slice(0, 5).map(caseItem => (
                    <div 
                      key={caseItem.case_id}
                      className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-violet-300 hover:bg-violet-50/50 transition-all cursor-pointer"
                      onClick={() => navigate('/cases')}
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">{caseItem.case_title}</p>
                        <p className="text-sm text-slate-600">Client: {caseItem.client_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-violet-700">
                          {new Date(caseItem.next_hearing_date).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-slate-500">
                          {Math.ceil((new Date(caseItem.next_hearing_date) - new Date()) / (1000 * 60 * 60 * 24))} days
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-lg">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  onClick={() => navigate('/clients')}
                  className="h-auto py-4 px-5 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white justify-start"
                >
                  <Users className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-semibold">Add Client</p>
                    <p className="text-xs opacity-90">New client intake</p>
                  </div>
                </Button>
                <Button
                  onClick={() => navigate('/cases')}
                  className="h-auto py-4 px-5 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white justify-start"
                >
                  <Briefcase className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-semibold">New Case</p>
                    <p className="text-xs opacity-90">Open new matter</p>
                  </div>
                </Button>
                <Button
                  onClick={() => navigate('/templates')}
                  className="h-auto py-4 px-5 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white justify-start"
                >
                  <FileText className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-semibold">Generate Document</p>
                    <p className="text-xs opacity-90">Create from template</p>
                  </div>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          /* Standard Dashboard */
          <>
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
          {/* Subscription Card */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg" data-testid="subscription-card">
            <div className="flex items-center justify-between mb-3">
              <Zap className="w-8 h-8 text-violet-600" />
              <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${currentPlan.color}`}>
                {currentPlan.name}
              </span>
            </div>
            <h3 className="text-sm font-medium text-slate-600 mb-1">Current Plan</h3>
            <p className="text-2xl font-bold">{currentPlan.name}</p>
            {user?.subscription_plan === 'free' && (
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full mt-4 h-10 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-bold"
                data-testid="upgrade-plan-btn"
              >
                Upgrade Plan
              </Button>
            )}
          </div>

          {/* Documents Used */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg" data-testid="docs-used-card">
            <TrendingUp className="w-8 h-8 text-indigo-600 mb-3" />
            <h3 className="text-sm font-medium text-slate-600 mb-1">Documents This Month</h3>
            <p className="text-2xl font-bold">
              {user?.docs_used_this_month || 0}
              {user?.subscription_plan === 'free' && ' / 10'}
            </p>
          </div>

          {/* Total Templates */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg" data-testid="templates-count-card">
            <FileText className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-sm font-medium text-slate-600 mb-1">Total Templates</h3>
            <p className="text-2xl font-bold">{templates.length}</p>
          </div>

          {/* Recent Activity */}
          <div 
            onClick={() => navigate('/runs')}
            className="p-6 rounded-2xl bg-white border border-slate-200 shadow-lg hover:border-teal-500 hover:shadow-xl transition-all duration-300 cursor-pointer" 
            data-testid="recent-activity-card"
          >
            <Clock className="w-8 h-8 text-teal-600 mb-3" />
            <h3 className="text-sm font-medium text-slate-600 mb-1">Recent Runs</h3>
            <p className="text-2xl font-bold">{runs.length}</p>
            <p className="text-xs text-teal-600 mt-2">Click to view all →</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              onClick={() => navigate('/templates')}
              className="group p-8 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 text-white cursor-pointer hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
              data-testid="create-template-card"
            >
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                <Plus className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Create New Template</h3>
              <p className="text-violet-100">Upload a document and start building your form template</p>
            </div>

            <div
              onClick={() => navigate('/templates')}
              className="group p-8 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 text-white cursor-pointer hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
              data-testid="view-templates-card"
            >
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                <FolderOpen className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-2">View All Templates</h3>
              <p className="text-indigo-100">Browse and manage your existing templates</p>
            </div>
          </div>
        </div>

        {/* Recent Templates */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">Recent Templates</h2>
          {templates.length === 0 ? (
            <div className="p-12 rounded-2xl bg-white border border-slate-200 text-center" data-testid="empty-templates-state">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-700 mb-2">No templates yet</h3>
              <p className="text-slate-600 mb-6">Create your first template to get started</p>
              <Button
                onClick={() => navigate('/templates')}
                className="h-12 px-8 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-bold"
                data-testid="create-first-template-btn"
              >
                Create Template
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.slice(0, 6).map((template, index) => (
                <div
                  key={index}
                  className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-violet-500 hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => navigate(`/templates/${template.template_id}/edit`)}
                  data-testid={`template-card-${index}`}
                >
                  <FileText className="w-10 h-10 text-violet-600 mb-3" />
                  <h3 className="text-lg font-bold mb-2">{template.name}</h3>
                  <p className="text-sm text-slate-600">{template.description || 'No description'}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Runs */}
        {runs.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Recent Document Runs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {runs.slice(0, 6).map((run, index) => {
                const template = templates.find(t => t.template_id === run.template_id);
                return (
                  <div
                    key={index}
                    className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-green-500 hover:shadow-xl transition-all duration-300 cursor-pointer"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await fetch(`${API}/templates/${run.template_id}/export/${run.run_id}?format=docx`, {
                          credentials: 'include',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        
                        if (!response.ok) {
                          toast.error('Failed to download document');
                          return;
                        }
                        
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = run.generated_filename;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                        toast.success('Document downloaded successfully!');
                      } catch (error) {
                        toast.error('Download failed');
                      }
                    }}
                    data-testid={`run-card-${index}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <FileText className="w-10 h-10 text-green-600" />
                      <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold">
                        {run.status}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold mb-2">{template?.name || 'Template'}</h3>
                    <p className="text-sm text-slate-600 mb-2">{run.generated_filename}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(run.created_at).toLocaleString()}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
          </>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;